import { Box, IconButton, Modal } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Fullscreen, Pause, PlayArrow } from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';

const Overlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    display: 'flex',
    zIndex: 2,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    cursor: 'pointer',
    '&:hover > .MuiBox-root': {
        opacity: 1,
    },
}));

const PlayPauseButton = styled(Box)<{ small?: boolean }>(({ theme, small }) => ({
    '&::before': {
        content: '""',
        display: 'block',
        width: small ? 24 + 8 : 56 + 16,
        height: small ? 24 + 8 : 56 + 16,
        // outline style transparent
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        position: 'absolute',
        zIndex: -1,
        borderRadius: '50%',
    },
    backgroundColor: 'rgba(255, 255, 255, 1)',
    width: small ? 24 : 56,
    height: small ? 24 : 56,
    // circle
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 0,
    transition: 'opacity 0.3s',
    zIndex: 2,
}));

export const VideoContainer = ({
    url,
    borderRadius = 16,
    ...props
}: {
    url: string;
    main?: boolean;
    small?: boolean;
    width?: number | string;
    height?: number | string;
    borderRadius?: number;
    onClick?: () => void;
}) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [play, setPlay] = useState(false);

    useEffect(() => {
        const video = videoRef.current;
        if (video) {
            video.load();
            setPlay(false);
        }
    }, [url]);

    const handlePlay = () => {
        const video = videoRef.current;
        if (!video) {
            return;
        }
        if (video.paused) {
            video.play();
            setPlay(true);
        } else {
            video.pause();
            setPlay(false);
        }
    };

    return (
        <>
            <Modal
                open={showModal}
                onClose={() => {
                    setShowModal(false);
                }}>
                <Box
                    sx={{
                        position: 'absolute' as 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '100%',
                        maxWidth: '1080px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}>
                    <video height="50%" controls autoPlay>
                        <source src={url} type="video/mp4" />
                        Video unsupported
                    </video>
                </Box>
            </Modal>
            <div
                style={{
                    flex: 1,
                    overflow: 'hidden',
                    width: props.width || Math.round(1920 * 0.2),
                    height: props.height || Math.round(1080 * 0.2),
                    zIndex: 1,
                    position: 'relative',
                    backgroundColor: 'black',
                    borderRadius,
                    borderBottomLeftRadius: props.main ? 0 : borderRadius,
                    borderBottomRightRadius: props.main ? 0 : borderRadius,
                }}>
                <Overlay
                    onClick={() => {
                        if (props.onClick) {
                            props.onClick();
                            return;
                        }
                        handlePlay();
                    }}>
                    {!props.small && (
                        <PlayPauseButton small={props.small}>
                            {play ? (
                                <i className="fi-sr-pause" style={{ fontSize: '1.5rem' }} />
                            ) : (
                                <i className="fi-sr-play" style={{ fontSize: '1.5rem' }} />
                            )}
                        </PlayPauseButton>
                    )}
                </Overlay>

                {/* Add button to show modal on top right*/}
                {!props.small && (
                    <IconButton
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            zIndex: 2,
                        }}
                        color="info"
                        onClick={() => {
                            setShowModal(true);
                        }}>
                        <i className="fi-br-expand" style={{ fontSize: '1.5rem' }} />
                    </IconButton>
                )}

                <video
                    width="100%"
                    ref={videoRef}
                    height="100%"
                    style={{
                        backgroundColor: 'rgba(0, 0, 0, 1)',
                        borderRadius: '8px',
                        objectFit: 'cover',
                    }}>
                    <source src={url} type="video/mp4" />
                    Video unsupported
                </video>
            </div>
        </>
    );
};

export const MultipleVideoContainer = ({
    urls,
    borderRadius = 16,
    ...props
}: {
    urls: string[];
    main?: boolean;
    small?: boolean;
    width?: number | string;
    height?: number | string;
    borderRadius?: number;
    onClick?: () => void;
}) => {
    const [main, setMain] = useState(urls[0]);

    useEffect(() => {
        setMain(urls[0]);
    }, [urls]);

    return (
        <>
            <div
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                <VideoContainer url={main} main />
            </div>
            {urls.length > 1 && (
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        marginTop: 1,
                        justifyContent: 'center',
                    }}>
                    {urls.map((url, index) => (
                        <VideoContainer
                            key={index}
                            url={url}
                            small
                            onClick={() => {
                                setMain(url);
                            }}
                        />
                    ))}
                </Box>
            )}
        </>
    );
};
